import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { NavigationPortal } from 'mastodon/components/navigation_portal';
import {
  identityContextPropShape,
  withIdentity,
} from 'mastodon/identity_context';
import { me, timelinePreview, trendsEnabled } from 'mastodon/initial_state';
import { transientSingleColumn } from 'mastodon/is_mobile';
import ColumnLink from './column_link';
import DisabledAccountBanner from './disabled_account_banner';
import { ListPanel } from './list_panel';
import NavigationPanel2 from './navigation_panel_2';
import SignInBanner from './sign_in_banner';
import { selectUnreadNotificationGroupsCount } from 'mastodon/selectors/notifications';
import { useSidebar } from 'mastodon/components/use-sidebar';
import { ListIcon } from 'mastodon/components/icons/list';
import { NotificationIcon } from 'mastodon/components/icons/notification';
import { HomeIcon } from 'mastodon/components/icons/home';
import { SearchIcon } from 'mastodon/components/icons/search';
import { LiveFeedIcon } from 'mastodon/components/icons/liveFeed';
import { ExploreIcon } from 'mastodon/components/icons/explore';
import { CommentIcon } from 'mastodon/components/icons/comment';
import { LikeIcon } from 'mastodon/components/icons/like';
import { BookmarkIcon } from 'mastodon/components/icons/bookmark';
import { LikeActiveIcon } from 'mastodon/components/icons/like_active';
import { Avatar } from 'mastodon/components/avatar';
import ComposeFormTrigger from 'mastodon/features/compose/components/compose_form_trigger';
import { LogoutIcon } from 'mastodon/components/icons/logout';
import { openModal } from 'mastodon/actions/modal';
import { LogoIcon } from 'mastodon/components/icons/logo';
import { ChevronLeftIcon } from 'mastodon/components/icons/chevron_left';
import { browserHistory } from 'mastodon/components/router';
import { LanguageIcon } from 'mastodon/components/icons/language';
import { useEffect, useState } from 'react';
import api from 'mastodon/api';
import { useAppDispatch } from 'mastodon/store';
import { ThemeIcon } from 'mastodon/components/icons/theme';
import { LoadingIndicator } from 'mastodon/components/loading_indicator';
import { Icon } from 'mastodon/components/icon';

const formatNumber = (num) => (num > 40 ? '40+' : num);

const messages = defineMessages({
  home: { id: 'tabs_bar.home', defaultMessage: 'Home' },
  notifications: {
    id: 'tabs_bar.notifications',
    defaultMessage: 'Notifications',
  },
  explore: { id: 'explore.title', defaultMessage: 'Explore' },
  firehose: { id: 'column.firehose', defaultMessage: 'Live feeds' },
  direct: { id: 'navigation_bar.direct', defaultMessage: 'Private mentions' },
  favourites: { id: 'navigation_bar.favourites', defaultMessage: 'Favorites' },
  bookmarks: { id: 'navigation_bar.bookmarks', defaultMessage: 'Bookmarks' },
  lists: { id: 'navigation_bar.lists', defaultMessage: 'Lists' },
  preferences: {
    id: 'navigation_bar.preferences',
    defaultMessage: 'Preferences',
  },
  administration: {
    id: 'navigation_bar.administration',
    defaultMessage: 'Administration',
  },
  moderation: { id: 'navigation_bar.moderation', defaultMessage: 'Moderation' },
  followsAndFollowers: {
    id: 'navigation_bar.follows_and_followers',
    defaultMessage: 'Follows and followers',
  },
  about: { id: 'navigation_bar.about', defaultMessage: 'About' },
  search: { id: 'navigation_bar.search', defaultMessage: 'Search' },
  advancedInterface: {
    id: 'navigation_bar.advanced_interface',
    defaultMessage: 'Open in advanced web interface',
  },
  openedInClassicInterface: {
    id: 'navigation_bar.opened_in_classic_interface',
    defaultMessage:
      'Posts, accounts, and other specific pages are opened by default in the classic web interface.',
  },
  followRequests: {
    id: 'navigation_bar.follow_requests',
    defaultMessage: 'Follow requests',
  },
  logout: { id: 'navigation_bar.logout', defaultMessage: 'Logout' },
  lao: { id: 'language.lao', defaultMessage: 'Lao' },
  vietnamese: { id: 'language.vietnamese', defaultMessage: 'Vietnamese' },
  english: { id: 'language.english', defaultMessage: 'English' },
  light: { id: 'theme.light', defaultMessage: 'Light' },
  dark: { id: 'theme.dark', defaultMessage: 'Dark' },
  system: { id: 'theme.system', defaultMessage: 'System' },
});

const mapStateToProps = (state) => ({
  notificationCount: selectUnreadNotificationGroupsCount(state),
  account: me ? state.getIn(['accounts', me]) : undefined,
});

const NavigationPanel = ({ intl, notificationCount, identity, account }) => {
  const location = useLocation();
  const dispatch = useAppDispatch();

  const { closeSidebar } = useSidebar();

  const [locale, setLocale] = useState('en');
  const [theme, setTheme] = useState('system');

  const [loading, setLoading] = useState(true);

  const isFirehoseActive = (match) => {
    return match || location.pathname.startsWith('/public');
  };

  const isActiveExplore = (match) => {
    return match || location.pathname.startsWith('/explore');
  };

  const { signedIn, disabledAccountId } = identity;

  let banner = undefined;

  if (transientSingleColumn) {
    banner = (
      <div className='switch-to-advanced'>
        {intl.formatMessage(messages.openedInClassicInterface)}{' '}
        <a
          href={`/deck${location.pathname}`}
          className='switch-to-advanced__toggle'
        >
          {intl.formatMessage(messages.advancedInterface)}
        </a>
      </div>
    );
  }

  const handleLogoutClick = e => {
    e.preventDefault();
    e.stopPropagation();

    dispatch(openModal({ modalType: 'CONFIRM_LOG_OUT' }));

    return false;
  };

  const ListActiveIcon = () => <ListIcon className='icon-active' />;

  const NotificationActiveIcon = () => <NotificationIcon className='icon-active icon-active-notify' />;

  const HomeActiveIcon = () => <HomeIcon className='icon-active' />;

  const SearchActiveIcon = () => <SearchIcon className='icon-active' />;

  const LiveFeedActiveIcon = () => <LiveFeedIcon className='icon-active' />;

  const ExploreActiveIcon = () => <ExploreIcon className='icon-active' />;

  const CommentActiveIcon = () => <CommentIcon className='icon-active' />;

  const BookmarkActiveIcon = () => <BookmarkIcon className='icon-active' />;

  const displayNameHtml = signedIn && { __html: account.display_name_html };

  useEffect(() => {
    api().get('/api/v1/preferences')
      .then((response) => {
        setLocale(response.data.locale);
        setTheme(response.data.theme);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching preferences:', error);
        setLoading(false);
      });
  }, []);


  const handleOpenChangeLanguage = (e) => {
    e.stopPropagation();
    closeSidebar();

    dispatch(openModal({
      modalType: 'LANGUAGE',
      modalProps: {
        currentLocale: locale,
      },
    }));
  };

  const handleOpenChangeTheme = (e) => {
    e.stopPropagation();
    closeSidebar();

    dispatch(openModal({
      modalType: 'THEME',
      modalProps: {
        currentTheme: theme,
      },
    }));
  };

  const handleHomeClick = () => {
    window.dispatchEvent(new Event('mastodon:home-suggested-feed'));
    closeSidebar();
  };

  const languages = [
    { code: 'lo', label: messages.lao },
    { code: 'vi', label: messages.vietnamese },
    { code: 'en', label: messages.english },
  ];

  const themes = [
    { code: 'mastodon-light', label: messages.light },
    { code: 'default', label: messages.dark },
    // { code: 'theme-system', label: messages.system },
  ];

  const selectedLanguage = languages.find((lang) => lang.code === locale);
  const selectedTheme = themes.find(item => item.code === theme);

  return (
    <div className='navigation-panel'>
      <div className='navigation-panel__logo'>
        <LogoIcon />
      </div>

      {banner && <div className='navigation-panel__banner'>{banner}</div>}

      {signedIn && <div className='navigation-profile'>
        <div className='account__header'>
          <div
            className='avatar'
            onClick={() => {
              browserHistory.push(`/@${account.username}`)
              closeSidebar()
            }}
          >
            <Avatar
              account={account}
              size={50}
            />
          </div>
          <div className='account__header__name'>
            <h1>
              <span dangerouslySetInnerHTML={displayNameHtml} />
            </h1>
            {window.innerWidth >= 630 && (
              <div className='navigation-setting-container'>
                <div className='navigation-setting language' onClick={handleOpenChangeLanguage}>
                  <LanguageIcon />
                  <p>{selectedLanguage?.label && intl.formatMessage(selectedLanguage.label)}</p>
                </div>

                <div className='navigation-setting theme' onClick={handleOpenChangeTheme}>
                  <ThemeIcon />
                  <p>{selectedTheme?.label && intl.formatMessage(selectedTheme.label)}</p>
                </div>
              </div>
            )}
          </div>
          <FormattedMessage id='welcome' defaultMessage='Welcome to LaoSocial' />
        </div>
        {window.innerWidth <= 630 && <ComposeFormTrigger isNavigation />}
      </div>
      }

      <div className='navigation-panel__menu'>
        {signedIn ? (
          <ColumnLink
            transparent
            to='/home'
            icon='home'
            iconComponent={HomeIcon}
            activeIconComponent={HomeActiveIcon}
            text={intl.formatMessage(messages.home)}
            onClick={handleHomeClick}
            style={{ touchAction: 'manipulation', userSelect: 'none' }}
          />
        ) : (
          <ColumnLink
            transparent
            to='/public/local'
            icon='globe'
            iconComponent={LiveFeedIcon}
            activeIconComponent={LiveFeedActiveIcon}
            text={intl.formatMessage(messages.firehose)}
            onClick={closeSidebar}
            style={{ touchAction: 'manipulation', userSelect: 'none' }}
          />
        )}

        {!trendsEnabled && (
          <ColumnLink
            transparent
            to='/search'
            icon='search'
            iconComponent={SearchIcon}
            activeIconComponent={SearchActiveIcon}
            text={intl.formatMessage(messages.search)}
            onClick={closeSidebar}
            style={{ touchAction: 'manipulation', userSelect: 'none' }}
          />
        )}

        {signedIn && timelinePreview && (
          <ColumnLink
            transparent
            to='/public/local'
            icon='globe'
            isActive={isFirehoseActive}
            iconComponent={LiveFeedIcon}
            activeIconComponent={LiveFeedActiveIcon}
            text={intl.formatMessage(messages.firehose)}
            onClick={closeSidebar}
            style={{ touchAction: 'manipulation', userSelect: 'none' }}
          />
        )}

        {!signedIn && (
          <div className='navigation-panel__sign-in-banner'>
            <hr />
            {disabledAccountId ? <DisabledAccountBanner /> : <SignInBanner />}
          </div>
        )}

        {signedIn && (
          <>
            {window.innerWidth > 768 && (
              <ColumnLink
                onClick={closeSidebar}
                style={{ touchAction: 'manipulation', userSelect: 'none' }}
                transparent
                to='/notifications'
                iconComponent={NotificationIcon}
                activeIconComponent={NotificationActiveIcon}
                text={intl.formatMessage(messages.notifications)}
                badge={
                  notificationCount > 0 && (
                    <i className='column-link-badge__badge'>
                      {formatNumber(notificationCount)}
                    </i>
                  )
                }
              />
            )}
            <ColumnLink
              onClick={closeSidebar}
              style={{ touchAction: 'manipulation', userSelect: 'none' }}
              transparent
              icon='explore'
              iconComponent={ExploreIcon}
              activeIconComponent={ExploreActiveIcon}
              text={intl.formatMessage(messages.explore)}
              to='/explore'
              isActive={isActiveExplore}
            />

            {/* <ColumnLink
              onClick={closeSidebar}
              transparent
              to='/conversations'
              icon='at'
              iconComponent={CommentIcon}
              activeIconComponent={CommentActiveIcon}
              text={intl.formatMessage(messages.direct)}
            /> */}
            <ColumnLink
              onClick={closeSidebar}
              style={{ touchAction: 'manipulation', userSelect: 'none' }}
              transparent
              to='/bookmarks'
              icon='bookmarks'
              iconComponent={BookmarkIcon}
              activeIconComponent={BookmarkActiveIcon}
              text={intl.formatMessage(messages.bookmarks)}
            />
            <ColumnLink
              onClick={closeSidebar}
              style={{ touchAction: 'manipulation', userSelect: 'none' }}
              transparent
              to='/favourites'
              icon='star'
              iconComponent={LikeIcon}
              activeIconComponent={LikeActiveIcon}
              text={intl.formatMessage(messages.favourites)}
            />
            <ColumnLink
              onClick={closeSidebar}
              style={{ touchAction: 'manipulation', userSelect: 'none' }}
              transparent
              to='/lists'
              icon='list-ul'
              iconComponent={ListIcon}
              activeIconComponent={ListActiveIcon}
              text={intl.formatMessage(messages.lists)}
            />
            <NavigationPanel2 closeSidebar={closeSidebar} />
            <ListPanel closeSidebar={closeSidebar} />

            <hr />
            {window.innerWidth >= 768 &&
              (
                <div onClick={handleLogoutClick} className='column-link' style={{ cursor: "pointer" }}>
                  <Icon id='sign-out' icon={LogoutIcon} className='column-link__icon' />
                  <span>{intl.formatMessage(messages.logout)}</span>
                </div>
              )
            }
          </>
        )}
      </div>

      <div className='flex-spacer' />

      {/* <NavigationPortal /> */}

      {window.innerWidth <= 630 && (
        <>
          <button className='sidebar-toggle-button' onClick={closeSidebar} style={{ touchAction: 'manipulation', userSelect: 'none' }}>
            <ChevronLeftIcon />
          </button>
          <div className='navigation-setting-container'>
            <div className='navigation-setting language' onClick={handleOpenChangeLanguage}>
              <LanguageIcon />
              <p>{selectedLanguage?.label && intl.formatMessage(selectedLanguage.label)}</p>
            </div>

            <div className='navigation-setting theme' onClick={handleOpenChangeTheme}>
              <ThemeIcon />
              <p>{selectedTheme?.label && intl.formatMessage(selectedTheme.label)}</p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

NavigationPanel.propTypes = {
  identity: identityContextPropShape,
  intl: PropTypes.object.isRequired,
  notificationCount: PropTypes.number,
  account: PropTypes.map,
};

export default connect(mapStateToProps)(injectIntl(withIdentity(NavigationPanel)));
